import { Controller } from "@hotwired/stimulus"
import {
  draggable,
  dropTargetForElements
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter"

// Drag tickets to reorder, drop onto another ticket to group them, drop onto a
// group to join it, or drop into a column's empty space to move/ungroup. The
// server is the source of truth: on drop we POST the move and let the Turbo
// Stream broadcast re-render. No optimistic DOM mutation to reconcile.
//
// Scoped per column (the items container). Pragmatic DnD's adapter is global,
// so draggables registered in one column can drop onto targets in another.
//
// Registrations bind to the elements present at the time, and a morph refresh
// adds/removes cards without reconnecting this controller, so we tear down
// and re-register after every morph.
export default class extends Controller {
  static values = { board: Number }

  connect() {
    this.register()
    this.remorph = () => {
      this.teardown()
      this.register()
    }
    document.addEventListener("turbo:morph", this.remorph)
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.remorph)
    this.teardown()
  }

  register() {
    this.cleanups = []
    this.registerColumn()
    this.element.querySelectorAll("[data-dnd-draggable]").forEach((el) => this.registerTicket(el))
    this.element.querySelectorAll("[data-dnd-target='group']").forEach((el) => this.registerGroup(el))
  }

  teardown() {
    this.cleanups.forEach((fn) => fn())
    this.cleanups = []
  }

  // Each ticket is both draggable and a drop target (drop onto it = group).
  registerTicket(el) {
    const ticketId = el.dataset.ticketId

    this.cleanups.push(
      draggable({
        element: el,
        getInitialData: () => ({ ticketId }),
        onDragStart: () => el.classList.add("opacity-40"),
        onDrop: () => el.classList.remove("opacity-40")
      })
    )

    this.cleanups.push(
      dropTargetForElements({
        element: el,
        getData: () => ({ kind: "ticket", ticketId }),
        onDragEnter: () => el.classList.add("ring-2", "ring-indigo-400"),
        onDragLeave: () => el.classList.remove("ring-2", "ring-indigo-400"),
        onDrop: (args) => {
          el.classList.remove("ring-2", "ring-indigo-400")
          if (!this.isInnermost(el, args)) return
          const sourceId = args.source.data.ticketId
          if (sourceId === ticketId) return
          this.move(sourceId, { onto_ticket_id: ticketId })
        }
      })
    )
  }

  registerGroup(el) {
    const groupId = el.dataset.groupId

    this.cleanups.push(
      dropTargetForElements({
        element: el,
        getData: () => ({ kind: "group", groupId }),
        onDragEnter: () => el.classList.add("ring-2", "ring-indigo-400"),
        onDragLeave: () => el.classList.remove("ring-2", "ring-indigo-400"),
        onDrop: (args) => {
          el.classList.remove("ring-2", "ring-indigo-400")
          if (!this.isInnermost(el, args)) return
          this.move(args.source.data.ticketId, { group_id: groupId })
        }
      })
    )
  }

  // The column itself: dropping in empty space moves the ticket here / ungroups.
  registerColumn() {
    const category = this.element.dataset.category

    this.cleanups.push(
      dropTargetForElements({
        element: this.element,
        getData: () => ({ kind: "column", category }),
        onDrop: (args) => {
          if (!this.isInnermost(this.element, args)) return
          this.move(args.source.data.ticketId, { category })
        }
      })
    )
  }

  // Only the deepest drop target under the pointer acts on the drop.
  isInnermost(el, args) {
    return args.location.current.dropTargets[0]?.element === el
  }

  async move(ticketId, params) {
    const body = new FormData()
    Object.entries(params).forEach(([key, value]) => body.append(key, value))

    await fetch(`/boards/${this.boardValue}/tickets/${ticketId}/move`, {
      method: "POST",
      headers: { "X-CSRF-Token": this.csrfToken },
      body
    })
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }
}
